import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as MuiButton,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Typography, PageHeader } from '@/components/ui';
import type { PageHeaderAction } from '@/components/ui/PageHeader';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import DataTable from '@/components/common/DataTable';
import type { TableColumn } from '@/components/common/DataTable/types';
import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import type { TableAction } from '@/components/common/DataTable/types';
import { PermissionFormDrawer } from '../../components';
import { permissionsService } from '@/superadmin/services/permissionsService';
import { toast } from 'react-hot-toast';
import { useSuperadminAuth } from '@/superadmin/contexts/SuperadminAuthContext';

export interface PermissionRow extends Record<string, unknown> {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const initialPermissions: PermissionRow[] = [];

const PermissionsPage: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<PermissionRow | null>(null);
  const [rows, setRows] = useState<PermissionRow[]>(initialPermissions);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<PermissionRow | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState<string | undefined>(undefined);

  const { user } = useSuperadminAuth();

  if (!user) return <div>No Access</div>;

  const hasPermission = (permission: string) => {
    if (user.is_superuser) return true; // superuser has everything
    return user.permission_list?.includes(permission);
  };


  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setLoading(true);
        const resp = await permissionsService.list(currentPage, pageSize, appliedSearch);
        const apiRows = resp?.results || [];
        const mapped: PermissionRow[] = apiRows.map(p => ({
          id: p.uuid,
          name: p.permission_name,
          description: p.description,
          is_active: p.is_active,
          created_at: p.created_at,
          updated_at: p.updated_at,
        }));
        setRows(mapped);
        setTotalCount(resp?.count || 0);
      } catch (e) {
        console.warn('Failed to load permissions:', e);
        toast.error('Failed to load permissions');
      } finally {
        setLoading(false);
      }
    };
    fetchPermissions();
  }, [currentPage, pageSize, appliedSearch]);

  const breadcrumbConfig = getBreadcrumbConfig('/permissions');
  const breadcrumbs = useCommonBreadcrumbs({ hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs });
  const finalBreadcrumbs =
    breadcrumbs.length > 0
      ? breadcrumbs
      : [
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Permissions', active: true },
        ];

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Filter handlers
  const handleSearch = () => {
    setAppliedSearch(searchTerm.trim() || undefined);
    setCurrentPage(1); // Reset to first page when applying search
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setAppliedSearch(undefined);
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Toggle active status
  const handleToggleActive = async (permissionId: string, currentStatus: boolean) => {
    try {
      setToggling(permissionId);
      await permissionsService.toggleActive(permissionId, !currentStatus);

      // Update local state
      setRows(prevRows =>
        prevRows.map(row => (row.id === permissionId ? { ...row, is_active: !currentStatus } : row))
      );

      toast.success(`Permission ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.warn('Failed to toggle permission status:', error);
      toast.error('Failed to update permission status');
    } finally {
      setToggling(null);
    }
  };

  const columns: TableColumn<PermissionRow>[] = [
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 220,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2" fontWeight="medium">
          {String(params.value)}
        </Typography>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      minWidth: 320,
      sortable: false,
      renderCell: params => <Typography variant="body2">{String(params.value)}</Typography>,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      minWidth: 120,
      sortable: true,
      renderCell: params => {
        const canUpdateStatus = hasPermission('ADMIN_PERMISSION_STATUS_UPDATE');

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={params.value ? 'Active' : 'Inactive'}
              color={params.value ? 'success' : 'default'}
              size="small"
              variant="outlined"
            />

            {canUpdateStatus && (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(params.value)}
                    onChange={() => handleToggleActive(params.row.id, Boolean(params.value))}
                    size="small"
                  />
                }
                label=""
                sx={{ margin: 0 }}
              />
            )}
          </Box>
        );
      },
    },

    {
      field: 'created_at',
      headerName: 'Created At',
      minWidth: 180,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2">{new Date(String(params.value)).toLocaleString()}</Typography>
      ),
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      minWidth: 180,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2">{new Date(String(params.value)).toLocaleString()}</Typography>
      ),
    },
  ];

  const headerActions: PageHeaderAction[] = [
    hasPermission('ADMIN_PERMISSION_ADD') && {
      label: 'New Permission',
      icon: <AddIcon sx={{ fontSize: 16 }} />,
      onClick: () => {
        setEditing(null);
        setDrawerOpen(true);
      },
      variant: 'contained',
      size: 'medium',
    },
  ].filter(Boolean) as PageHeaderAction[];

  const actions: TableAction<PermissionRow>[] = [
    hasPermission('ADMIN_PERMISSION_EDIT') && {
      label: 'Edit',
      icon: <EditIcon />,
      color: 'primary',
      onClick: ({ row }) => {
        setEditing(row);
        setDrawerOpen(true);
      },
    },
    hasPermission('ADMIN_PERMISSION_DELETE') && {
      label: 'Delete',
      icon: <DeleteIcon />,
      color: 'error',
      onClick: ({ row }) => {
        setToDelete(row);
        setConfirmOpen(true);
      },
      danger: true,
    },
  ].filter(Boolean) as TableAction<PermissionRow>[];

  const handleSubmitPermission = async (payload: {
    id?: string;
    name: string;
    description: string;
  }) => {
    try {
      setLoading(true);
      if (editing) {
        await permissionsService.update(editing.id, {
          permission_name: payload.name,
          description: payload.description,
        });
        toast.success('Permission updated');
      } else {
        await permissionsService.create({
          permission_name: payload.name,
          description: payload.description,
        });
        toast.success('Permission created');
      }

      const resp = await permissionsService.list(currentPage, pageSize, appliedSearch);
      const apiRows = resp?.results || [];
      const mapped: PermissionRow[] = apiRows.map(p => ({
        id: p.uuid,
        name: p.permission_name,
        description: p.description,
        is_active: p.is_active,
        created_at: p.created_at,
        updated_at: p.updated_at,
      }));
      setRows(mapped);
      setTotalCount(resp?.count || 0);
      setDrawerOpen(false);
      setEditing(null);
    } catch (e) {
      toast.error('Failed to save permission');
      console.warn('Save permission failed:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Permissions"
        subtitle={`Manage permissions (${totalCount} ${totalCount === 1 ? 'item' : 'items'})`}
        breadcrumbs={finalBreadcrumbs}
        actions={headerActions}
        showDivider={false}
      />

      {/* Filter Controls */}
      <Box
        mb={3}
        display="flex"
        gap={2}
        alignItems="center"
        flexWrap="wrap"
        justifyContent="space-between"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      >
        <Box display="flex" gap={2} alignItems="center">
          {/* Search Filter */}
          <TextField
            variant="outlined"
            type="search"
            placeholder="Search permissions"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              maxWidth: 400,
              '& .MuiOutlinedInput-root': {
                background: 'white',
              },
            }}
          />

          {/* Search Button */}
          <MuiButton
            variant="contained"
            size="small"
            onClick={handleSearch}
            sx={{ ml: 1 }}
            disabled={loading}
          >
            {loading ? 'Searching...' : 'Search'}
          </MuiButton>
        </Box>
        <Box>
          {/* Clear Filters Button */}
          <MuiButton
            sx={{ mr: 8 }}
            variant="outlined"
            size="small"
            onClick={handleClearFilters}
            startIcon={<ClearIcon />}
          >
            Clear Filters
          </MuiButton>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={() => {
                handleSearch();
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {hasPermission('ADMIN_PERMISSION_LIST_VIEW') ? (
        <Box>
          <DataTable
            data={rows}
            columns={columns}
            actions={actions}
            keyField="id"
            title="Permissions"
            showTitle={false}
            loading={loading}
            pagination={true}
            rowsPerPageOptions={[5, 10, 15, 25, 50]}
            defaultRowsPerPage={15}
            page={currentPage - 1}
            rowsPerPage={pageSize}
            totalCount={totalCount}
            onPageChange={page => handlePageChange(page + 1)}
            onRowsPerPageChange={handlePageSizeChange}
            sortable={true}
            stickyHeader={true}
            maxHeight={600}
            striped={true}
            emptyState={
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <Typography variant="body1" color="textSecondary">
                  No permissions found.
                </Typography>
              </Box>
            }
          />
        </Box>
      ) : (
        <Typography variant="body1" color="textSecondary">
          You do not have permission to view.
        </Typography>
      )}
      <PermissionFormDrawer
        open={drawerOpen}
        initialValues={
          editing ? { id: editing.id, name: editing.name, description: editing.description } : null
        }
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmitPermission}
      />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete Permission</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{toDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConfirmOpen(false)}>Cancel</MuiButton>
          <MuiButton
            color="error"
            onClick={async () => {
              if (!toDelete) return;
              try {
                setLoading(true);
                await permissionsService.delete(toDelete.id);
                const resp = await permissionsService.list(currentPage, pageSize, appliedSearch);
                const apiRows = resp?.results || [];
                const mapped: PermissionRow[] = apiRows.map(p => ({
                  id: p.uuid,
                  name: p.permission_name,
                  description: p.description,
                  is_active: p.is_active,
                  created_at: p.created_at,
                  updated_at: p.updated_at,
                }));
                setRows(mapped);
                setTotalCount(resp?.count || 0);
                toast.success('Permission deleted');
              } catch (e) {
                toast.error('Failed to delete permission');
                console.warn('Delete permission failed:', e);
              } finally {
                setLoading(false);
                setConfirmOpen(false);
                setToDelete(null);
              }
            }}
          >
            Delete
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermissionsPage;
